// src/pages/EnvioMasivo.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas as QRCode } from "qrcode.react";
import * as XLSX from "xlsx";
import api from "../auth/api";
import TemplatePlantilla from "../components/TemplateEditor";
import { useAuth } from "../auth/AuthContext";

// ──────────────────────────────────────────────────────────────
// Utils de UI
// ──────────────────────────────────────────────────────────────
function setIfChanged(setter, next, prevRef) {
  const prev = prevRef.current;
  const same = JSON.stringify(prev) === JSON.stringify(next);
  if (!same) {
    prevRef.current = next;
    setter(next);
  }
}

function usePageVisible() {
  const [visible, setVisible] = useState(!document.hidden);
  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  return visible;
}

function HHMMSS(totalSec) {
  const s = Math.max(0, Math.round(totalSec));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// ETA: asumiendo envío secuencial con pausas por lote
function estimateETA({ remaining, perMessageDelaySec, batchSize, batchPauseMin }) {
  if (remaining <= 0) return { seconds: 0, finishAt: null };
  const batchesLeft = Math.ceil(remaining / Math.max(1, batchSize));
  const perMsg = Math.max(0, perMessageDelaySec);
  const pause = Math.max(0, batchPauseMin) * 60;
  const seconds = remaining * perMsg + Math.max(0, batchesLeft - 1) * pause;
  const finishAt = new Date(Date.now() + seconds * 1000);
  return { seconds, finishAt };
}

// Parse seguro de whatsapp_templates (string o array)
function parseWhatsAppTemplates(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeJson(value) {
  try {
    return typeof value === "string" ? JSON.parse(value || "{}") : value || {};
  } catch {
    return {};
  }
}

function accountWabaId(account) {
  const cfg = safeJson(account?.config_json);
  return String(
    cfg.WABA_ID ||
      cfg.waba_id ||
      cfg.WHATSAPP_BUSINESS_ACCOUNT_ID ||
      cfg.whatsapp_business_account_id ||
      ""
  ).trim();
}

// ──────────────────────────────────────────────────────────────
// Pantalla principal
// ──────────────────────────────────────────────────────────────
export default function EnvioMasivo() {
  const { user } = useAuth();
  const isVisible = usePageVisible();

  // --- cuentas / selección
  const [accounts, setAccounts] = useState([]);     // [{id,label,phone,provider,status}]
  const [selectedId, setSelectedId] = useState(""); // id de la cuenta en uso

  // --- estado de conexión/qr
  const [connected, setConnected] = useState(false);
  const [qr, setQr] = useState("");

  // --- envío / datos
  const [columns, setColumns] = useState([]);
  const [excelRows, setExcelRows] = useState([]);

  // Antes: template string único
  // Ahora: mantenemos compat pero usamos waList como fuente real
  const [template, setTemplate] = useState("Hola {nombre}, tenemos una promoción especial.");

  // Nuevo: lista de mensajes del template (whatsapp_templates)
  const [waList, setWaList] = useState([]); // [{id,name,text,delay}, ...]

  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  // Parámetros de envío
  const [perMessageDelaySec, setPerMessageDelaySec] = useState(60);
  const [batchSize, setBatchSize] = useState(20);
  const [batchPauseMin, setBatchPauseMin] = useState(10);

  // Plantillas
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // Batches y progreso
  const [batches, setBatches] = useState([]);
  const [batchesMine, setBatchesMine] = useState(true);
  const [currentBatchId, setCurrentBatchId] = useState("");
  const [currentBatchMeta, setCurrentBatchMeta] = useState(null);
  const [progress, setProgress] = useState({
    total: 0,
    sent: 0,
    pending: 0,
    error: 0,
    inProgress: false,
  });

  // Jobs (teléfonos) del lote
  const [jobsOpen, setJobsOpen] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsCounts, setJobsCounts] = useState({
    total: 0,
    sent: 0,
    pending: 0,
    error: 0,
  });
  const [jobsStatus, setJobsStatus] = useState("all");
  const [jobsQuery, setJobsQuery] = useState("");

  // Archivo seleccionado
  const [lastSelectedFileName, setLastSelectedFileName] = useState("");
  const [fileKey, setFileKey] = useState(0);
  const fileInputRef = useRef(null);

  // cache de rutas/capacidades
  const [capsMap, setCapsMap] = useState({});
  const pathCacheRef = useRef({
    status: {},
    qr: {},
    progress: {},
    rows: {},
    aSum: {},
    aSeries: {},
  });

  // polling
  const pollRef = useRef({ timer: null });

  // anti flicker
  const prevBatchesRef = useRef(null);
  const prevStatusRef = useRef(null);
  const prevProgRef = useRef(null);

  // Email opcional
  const [sendEmail, setSendEmail] = useState(false);
  const [templateEmailSubject, setTemplateEmailSubject] = useState("");
  const [templateEmailBody, setTemplateEmailBody] = useState("");

  // storage key por usuario+canal
  const STORAGE_KEY = (uid, acc) => `wa-batch:${uid || "anon"}:${acc || "na"}`;

  // ──────────────────────────────────────────────────────────────
  // Endpoints helper
  // ──────────────────────────────────────────────────────────────
  const ep = useMemo(() => {
    const noCache = {
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    };
    const bust = () => `?_=${Date.now()}`;
    const ok = (r) =>
      r && typeof r.status === "number" && r.status >= 200 && r.status < 300;
    const normConn = (d) =>
      d?.connected === true || d?.isConnected === true;
    const normQR = (d) =>
      d?.qr ?? d?.qrcode ?? d?.qrCode ?? d?.currentQR ?? d?.pairing?.qr ?? "";

    const tryGet = async (path) => {
      try {
        return await api.get(`${path}${bust()}`, noCache);
      } catch {
        return null;
      }
    };

    const getStatus = async (id) => {
      const cache = pathCacheRef.current.status[id];
      if (cache) {
        const res = await tryGet(cache);
        if (ok(res)) return res.data;
      }
      const candidates = [
        `/wa/accounts/${id}/status`,
        `/wa/${id}/status`,
        `/status`,
      ];
      for (const p of candidates) {
        const res = await tryGet(p);
        if (ok(res)) {
          pathCacheRef.current.status[id] = p;
          return res.data;
        }
      }
      return null;
    };

    const getQR = async (id) => {
      const cache = pathCacheRef.current.qr[id];
      if (cache) {
        const res = await tryGet(cache);
        if (ok(res)) {
          const d = res.data;
          return typeof d === "string" ? d : normQR(d) || "";
        }
      }
      const candidates = [
        `/wa/accounts/${id}/status`,
        `/wa/${id}/get-qr`,
        `/get-qr`,
        `/status`,
      ];
      for (const p of candidates) {
        const res = await tryGet(p);
        if (ok(res)) {
          pathCacheRef.current.qr[id] = p;
          const d = res.data;
          return typeof d === "string" ? d : normQR(d) || "";
        }
      }
      return "";
    };

    const probeCaps = async (id) => {
      const current = capsMap[id] || {};
      if (
        ["progress", "rows", "aSum", "aSeries"].every(
          (k) => k in current
        )
      )
        return current;

      const check = async (paths) => {
        for (const p of paths) {
          const res = await tryGet(p);
          if (ok(res)) return p;
        }
        return null;
      };

      const pProg = await check([
        `/wa/accounts/${id}/send/progress`,
        `/send/progress`,
      ]);
      const pRows = await check([`/wa/accounts/${id}/rows`, `/rows`]);
      const pASum = await check([
        `/wa/accounts/${id}/analytics/summary`,
        `/analytics/summary`,
      ]);
      const pASer = await check([
        `/wa/accounts/${id}/analytics/timeseries`,
        `/analytics/timeseries`,
      ]);

      if (pProg) pathCacheRef.current.progress[id] = pProg;
      if (pRows) pathCacheRef.current.rows[id] = pRows;
      if (pASum) pathCacheRef.current.aSum[id] = pASum;
      if (pASer) pathCacheRef.current.aSeries[id] = pASer;

      const caps = {
        progress: !!pProg,
        rows: !!pRows,
        aSum: !!pASum,
        aSeries: !!pASer,
      };
      setCapsMap((m) => ({ ...m, [id]: caps }));
      return caps;
    };

    const batchProgress = async (id, batchId) => {
      const r = await api.get(
        `/wa/accounts/${id}/batches/${batchId}/progress${bust()}`,
        noCache
      );
      return r?.data;
    };

    const listBatchJobs = async (
      id,
      batchId,
      { status = "", q = "", limit = 500, offset = 0 } = {}
    ) => {
      const qs = new URLSearchParams();
      if (status) qs.set("status", status);
      if (q) qs.set("q", q);
      if (limit) qs.set("limit", String(limit));
      if (offset) qs.set("offset", String(offset));
      const url = `/wa/accounts/${id}/batches/${batchId}/jobs?${qs.toString()}${
        qs.toString() ? "&" : ""
      }_=${Date.now()}`;
      const r = await api.get(url, noCache);
      return (
        r?.data || {
          rows: [],
          counts: { total: 0, sent: 0, pending: 0, error: 0 },
        }
      );
    };

    return {
      listAccounts: async () => {
        const r = await tryGet(`/wa/accounts`);
        return r?.data?.rows || [];
      },
      status: async (id) => {
        const d = await getStatus(id);
        return d
          ? { connected: normConn(d), qr: normQR(d) || "", raw: d }
          : { connected: false, qr: "" };
      },
      qr: async (id) => getQR(id),
      connect: async (id) => {
        try {
          await api.post(`/wa/accounts/${id}/connect`);
        } catch {
          await api.post(`/connect`).catch(() => {});
        }
      },

      // Plantillas
      loadTemplates: async () => {
        const r = await tryGet(`/templates/whatsapp`);
        return r?.data?.templates || [];
      },

      // Batches
      // Ahora acepta whatsapp_templates además del template clásico
      createBatch: async (id, { template, templateId, params, whatsapp_templates }) => {
        const payload = {
          template,          // compat anterior
          templateId,
          params,
          whatsapp_templates // NUEVO: arreglo JSON completo
        };
        const r = await api.post(`/wa/accounts/${id}/batches`, payload);
        return r?.data?.batchId;
      },

      importRowsToBatch: async (id, batchId, payload) => {
        const r = await api.post(
          `/wa/accounts/${id}/batches/${batchId}/import-rows`,
          payload
        );
        return r?.data;
      },

      batchStart: async (id, batchId, body) => {
        const r = await api.post(
          `/wa/accounts/${id}/batches/${batchId}/send/start`,
          body
        );
        return r?.data;
      },

      batchStop: async (id, batchId, { abandon = false } = {}) => {
        const r = await api.post(
          `/wa/accounts/${id}/batches/${batchId}/send/stop?abandon=${abandon}`
        );
        return r?.data;
      },

      listBatches: async (id, { mine = false } = {}) => {
        const qs = mine ? "?mine=1" : "";
        const r = await api.get(
          `/wa/accounts/${id}/batches${qs}${bust()}`,
          noCache
        );
        return r?.data?.rows || [];
      },

      batchProgress,
      listBatchJobs,
      probeCaps,
    };
  }, [capsMap]);

  // ──────────────────────────────────────────────────────────────
  // Carga inicial
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const rows = await ep.listAccounts();
      setAccounts(rows);
      if (rows.length) setSelectedId(String(rows[0].id));
      const tpls = await ep.loadTemplates();
      setTemplates(tpls);
    })();
  }, [ep]);

  // Restaurar último batch
  useEffect(() => {
    if (!selectedId) return;
    const saved =
      localStorage.getItem(STORAGE_KEY(user?.id, selectedId)) || "";
    setCurrentBatchId(saved);
    setCurrentBatchMeta(null);
    setJobs([]);
    setJobsCounts({ total: 0, sent: 0, pending: 0, error: 0 });
    setJobsOpen(!!saved);
  }, [selectedId, user?.id]);

  // Polling
  useEffect(() => {
    if (!selectedId || !isVisible) return;
    if (pollRef.current.timer)
      clearInterval(pollRef.current.timer);

    const TICK_MS = 3000;

    const loadJobs = async (batchIdArg) => {
      const batchId = String(batchIdArg || currentBatchId || "");
      if (!selectedId || !batchId) return;
      setJobsLoading(true);
      try {
        const status = jobsStatus === "all" ? "" : jobsStatus;
        const data = await ep.listBatchJobs(selectedId, batchId, {
          status,
          q: jobsQuery,
          limit: 1000,
          offset: 0,
        });
        setJobs(Array.isArray(data.rows) ? data.rows : []);
        setJobsCounts(
          data.counts || {
            total: 0,
            sent: 0,
            pending: 0,
            error: 0,
          }
        );
      } catch {
        setJobs([]);
        setJobsCounts({
          total: 0,
          sent: 0,
          pending: 0,
          error: 0,
        });
      } finally {
        setJobsLoading(false);
      }
    };

    const tick = async () => {
      const selectedAccount = accounts.find((a) => String(a.id) === String(selectedId));
      const selectedIsCloud = selectedAccount?.provider === "cloud";

      // Conexión
      try {
        if (selectedIsCloud) {
          setIfChanged(setConnected, true, prevStatusRef);
          setQr("");
        } else {
          const s = await ep.status(selectedId);
        setIfChanged(
          setConnected,
          !!s.connected,
          prevStatusRef
        );
        if (!s.connected) {
          const newQr = s.qr || "";
          setQr((old) => (old === newQr ? old : newQr));
        }
        }
      } catch {}

      // Lotes
      try {
        const list = await ep.listBatches(selectedId, {
          mine: batchesMine,
        });
        setIfChanged(setBatches, list, prevBatchesRef);

        if (!currentBatchId) {
          const candidate = list.find(
            (b) =>
              String(b.user_id) === String(user?.id) &&
              (b.status === "running" ||
                b.status === "created")
          );
          if (candidate) {
            setCurrentBatchId(String(candidate.id));
            localStorage.setItem(
              STORAGE_KEY(user?.id, selectedId),
              String(candidate.id)
            );
            setJobsOpen(true);
          }
        }
      } catch {}

      // Progreso
      if (currentBatchId) {
        try {
          const bp = await ep.batchProgress(
            selectedId,
            currentBatchId
          );
          const p = {
            total: bp?.total || 0,
            sent: bp?.sent || 0,
            pending: bp?.pending || 0,
            error: bp?.error || 0,
            inProgress:
              !!bp?.inProgress ||
              bp?.batch?.status === "running",
          };
          setIfChanged(setProgress, p, prevProgRef);
          setCurrentBatchMeta(bp?.batch || null);

          if (jobsOpen && p.inProgress) {
            await loadJobs(currentBatchId);
          }
        } catch {}
      }
    };

    tick();
    pollRef.current.timer = setInterval(tick, TICK_MS);

    return () => {
      if (pollRef.current.timer)
        clearInterval(pollRef.current.timer);
      pollRef.current.timer = null;
    };
  }, [
    selectedId,
    ep,
    isVisible,
    currentBatchId,
    batchesMine,
    jobsOpen,
    user?.id,
    jobsStatus,
    jobsQuery,
    accounts,
  ]);

  // ──────────────────────────────────────────────────────────────
  // Acciones
  // ──────────────────────────────────────────────────────────────
  const connect = async () => {
    if (!selectedId) return;
    await ep.connect(selectedId);
    setQr(await ep.qr(selectedId));
  };

  const handleFile = (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      setLastSelectedFileName(file.name || "");
      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          blankrows: false,
        });
        const header = (rows[0] || []).map((h) =>
          String(h ?? "").trim()
        );
        const body = rows
          .slice(1)
          .filter((r) =>
            r?.some(
              (x) => String(x ?? "").trim() !== ""
            )
          );
        const items = body.map((r) =>
          header.reduce(
            (acc, col, i) => ({
              ...acc,
              [col]: r[i],
            }),
            {}
          )
        );
        setColumns(header);
        setExcelRows(items);
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error(err);
      alert("No se pudo leer el Excel.");
    }
  };

  const clearViewForNew = () => {
    localStorage.removeItem(
      STORAGE_KEY(user?.id, selectedId)
    );
    setCurrentBatchId("");
    setCurrentBatchMeta(null);
    setProgress({
      total: 0,
      sent: 0,
      pending: 0,
      error: 0,
      inProgress: false,
    });
    setJobs([]);
    setJobsCounts({
      total: 0,
      sent: 0,
      pending: 0,
      error: 0,
    });
    setJobsOpen(false);
  };

  const loadJobs = async (batchIdArg) => {
    const batchId = String(
      batchIdArg || currentBatchId || ""
    );
    if (!selectedId || !batchId) return;
    setJobsLoading(true);
    try {
      const status =
        jobsStatus === "all" ? "" : jobsStatus;
      const data = await ep.listBatchJobs(
        selectedId,
        batchId,
        {
          status,
          q: jobsQuery,
          limit: 1000,
          offset: 0,
        }
      );
      setJobs(
        Array.isArray(data.rows) ? data.rows : []
      );
      setJobsCounts(
        data.counts || {
          total: 0,
          sent: 0,
          pending: 0,
          error: 0,
        }
      );
    } catch {
      setJobs([]);
      setJobsCounts({
        total: 0,
        sent: 0,
        pending: 0,
        error: 0,
      });
    } finally {
      setJobsLoading(false);
    }
  };

  // Crear+Importar+Enviar
  const createImportAndSend = async () => {
    const currentAccount = accounts.find(
      (a) => String(a.id) === String(selectedId)
    );
    const currentIsCloud = currentAccount?.provider === "cloud";

    if (!selectedId)
      return alert("Selecciona un canal.");
    if (!currentIsCloud && !connected && !sendEmail)
      return alert("Conecta el canal antes de enviar o activa el envío por email.");    
    if (!waList || waList.length === 0)
      return alert(
        "Selecciona o edita una plantilla con whatsapp_templates."
      );
    if (excelRows.length === 0)
      return alert(
        "Selecciona un Excel con teléfonos y columnas."
      );

    if (
      sendEmail &&
      !(
        templateEmailSubject.trim() ||
        templateEmailBody.trim()
      )
    ) {
      const ok = window.confirm(
        "Has activado 'Enviar email' pero Asunto/Cuerpo están vacíos. ¿Quieres continuar?"
      );
      if (!ok) return;
    }

    if (currentBatchId && progress.inProgress) {
      const ok = window.confirm(
        "Tienes un envío en curso. ¿Abandonar el lote actual y comenzar uno nuevo?"
      );
      if (!ok) return;
      try {
        await ep.batchStop(
          selectedId,
          currentBatchId,
          { abandon: true }
        );
      } catch {}
    }

    setUploading(true);
    setSending(true);

    try {
      const selectedTemplate = (templates || []).find(
        (t) => String(t.id) === String(selectedTemplateId)
      );
      const isCloudBusinessTemplate =
        currentIsCloud && Number(selectedTemplate?.is_whatsapp_business || 0) === 1;

      if (currentIsCloud) {
        if (!selectedTemplate) {
          return alert("Selecciona una plantilla aprobada de WhatsApp Business API.");
        }
        if (!isCloudBusinessTemplate || selectedTemplate.status !== "approved") {
          return alert("Cloud API masivo solo permite plantillas WhatsApp Business aprobadas por Meta.");
        }
        if (selectedTemplate.meta_status && selectedTemplate.meta_status !== "APPROVED") {
          return alert(`La plantilla todavía no está aprobada en Meta. Estado: ${selectedTemplate.meta_status}`);
        }
      }

      // 1) Crear lote con whatsapp_templates incluido
      const batchId = await ep.createBatch(
        selectedId,
        {
          template, // compat, aunque el backend puede ignorarlo
          templateId:
            selectedTemplateId || null,
          whatsapp_templates: waList,
          params: {
            perMessageDelaySec,
            batchSize,
            batchPauseMin,
            source_file_name:
              lastSelectedFileName ||
              "",
            sendEmail,
            emailSubjectTemplate:
              templateEmailSubject ||
              "",
            emailBodyTemplate:
              templateEmailBody ||
              "",
            cloudTemplateName:
              isCloudBusinessTemplate
                ? String(selectedTemplate?.name || "").trim()
                : "",
            cloudTemplateLang: "es",
          },
        }
      );

      // 2) Guardar selección local
      localStorage.setItem(
        STORAGE_KEY(user?.id, selectedId),
        String(batchId)
      );
      setCurrentBatchId(String(batchId));
      setJobsOpen(true);

      // 3) Importar filas
      const d = await ep.importRowsToBatch(
        selectedId,
        batchId,
        {
          columns,
          rows: excelRows,
        }
      );

      setExcelRows([]);
      setColumns([]);

      // 4) Lanzar envío
      const perMessageDelayMs = Math.max(
        0,
        Math.round(perMessageDelaySec * 1000)
      );
      const batchPauseMs = Math.max(
        0,
        Math.round(
          batchPauseMin * 60 * 1000
        )
      );

      await ep.batchStart(
        selectedId,
        batchId,
        {
          perMessageDelayMs,
          batchSize,
          batchPauseMs,
          userId: user?.id,
          sendEmail,
        }
      );

      // 5) Resumen
      alert(
        `Importados: ${d.imported}\nSin objeto: ${d.skippedNoObject}\nSin teléfono: ${d.skippedNoPhone}\nJSON error: ${d.skippedSerialize}\nErrores: ${
          (d.errors || []).join("\n") ||
          "—"
        }`
      );

      await loadJobs(batchId);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        "No se pudo iniciar el envío.";
      alert(msg);
    } finally {
      setUploading(false);
      setSending(false);
    }
  };

  const stopSending = async () => {
    if (!selectedId || !currentBatchId) return;
    const abandon =
      window.confirm(
        "¿Deseas abandonar el lote actual? Los pendientes quedarán como 'aborted'."
      ) || false;
    await ep.batchStop(
      selectedId,
      currentBatchId,
      { abandon }
    );
  };

  const activateBatch = (b) => {
    localStorage.setItem(
      STORAGE_KEY(user?.id, selectedId),
      String(b.id)
    );
    setCurrentBatchId(String(b.id));
    setCurrentBatchMeta(b);
    setJobsOpen(true);
  };

  // ──────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────
  const selectedAcc = accounts.find(
    (a) =>
      String(a.id) ===
      String(selectedId)
  );
  const isBaileys =
    selectedAcc?.provider !== "cloud";
  const selectedWabaId = accountWabaId(selectedAcc);
  const visibleTemplates = (templates || []).filter((t) => {
    if (isBaileys) return Number(t.is_whatsapp || 0) === 1 || Number(t.is_email || 0) === 1;
    return (
      Number(t.is_whatsapp_business || 0) === 1 &&
      String(t.status || "").toLowerCase() === "approved" &&
      (!t.meta_status || String(t.meta_status).toUpperCase() === "APPROVED") &&
      (!selectedWabaId || String(t.meta_waba_id || "").trim() === selectedWabaId)
    );
  });

  const remaining = Math.max(
    0,
    (progress.total || 0) -
      (progress.sent || 0) -
      (progress.error || 0)
  );
  const eta = estimateETA({
    remaining,
    perMessageDelaySec,
    batchSize,
    batchPauseMin,
  });

  return (
    <>
      <div className="page-title">
        Envío masivo
      </div>

      {/* Selector de canal */}
      <section
        className="card section"
        style={{ marginBottom: 14 }}
      >
        {accounts.length === 0 ? (
          <div className="muted">
            No hay canales. Ve a{" "}
            <b>Ajustes</b> &gt;{" "}
            <b>Nuevo canal</b> para
            crear uno.
          </div>
        ) : (
          <div
            className="row row-3"
            style={{ alignItems: "end" }}
          >
            <label>
              Canal
              <select
                className="input"
                value={selectedId}
                onChange={(e) => {
                  setSelectedId(
                    e.target.value
                  );
                  clearViewForNew();
                }}
              >
                {(accounts || []).map(
                  (a) => (
                    <option
                      key={a.id}
                      value={a.id}
                    >
                      {a.label} ·{" "}
                      {a.provider ===
                      "cloud"
                        ? "Cloud"
                        : "Baileys"}{" "}
                      · {a.phone}
                    </option>
                  )
                )}
              </select>
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  display:
                    "inline-block",
                  width: 12,
                  height: 12,
                  borderRadius:
                    "50%",
                  background:
                    connected
                      ? "#22c55e"
                      : "#ef4444",
                  border:
                    "2px solid #0f172a",
                }}
              />
              <div
                style={{
                  opacity: 0.85,
                }}
              >
                {connected
                  ? "Conectado"
                  : "Desconectado"}
              </div>
              {isBaileys && (
                <button
                  className="btn btn-primary"
                  onClick={connect}
                >
                  Conectar
                  WhatsApp
                </button>
              )}
            </div>
          </div>
        )}

        {!connected &&
          isBaileys &&
          qr && (
            <div
              style={{
                marginTop: 10,
                textAlign:
                  "center",
              }}
            >
              <div
                style={{
                  marginBottom: 6,
                  color:
                    "var(--muted)",
                }}
              >
                Escanea este
                QR
              </div>
              {qr.startsWith(
                "data:image"
              ) ||
              qr.startsWith(
                "http"
              ) ? (
                <img
                  src={qr}
                  alt="QR"
                  width={200}
                  height={200}
                />
              ) : (
                <QRCode
                  value={qr}
                  size={200}
                />
              )}
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  color:
                    "var(--muted)",
                }}
              >
                WhatsApp &gt;
                Dispositivos
                vinculados
                &gt; Vincular
                dispositivo
              </div>
            </div>
          )}
      </section>

      {/* Parámetros */}
      <div className="row row-2">
        <section className="card section">
          <h3
            style={{ marginTop: 0 }}
          >
            Parámetros de
            envío
          </h3>
          <div className="row row-3">
            <label>
              Delay por
              mensaje (seg)
              <input
                className="input"
                type="number"
                min={0}
                max={300}
                value={
                  perMessageDelaySec
                }
                onChange={(e) =>
                  setPerMessageDelaySec(
                    Number(
                      e.target.value
                    )
                  )
                }
              />
            </label>
            <label>
              Tamaño de lote
              <input
                className="input"
                type="number"
                min={1}
                max={500}
                value={
                  batchSize
                }
                onChange={(e) =>
                  setBatchSize(
                    Number(
                      e.target.value
                    )
                  )
                }
              />
            </label>
            <label>
              Pausa entre
              lotes (min)
              <input
                className="input"
                type="number"
                min={0}
                max={60}
                value={
                  batchPauseMin
                }
                onChange={(e) =>
                  setBatchPauseMin(
                    Number(
                      e.target.value
                    )
                  )
                }
              />
            </label>
          </div>
        </section>
      </div>

      {/* Plantilla */}
      <section className="card section">
        <div
          className="row row-3"
          style={{
            marginBottom: 10,
          }}
        >
          <label>
            Plantillas
            <select
              className="input"
              value={
                selectedTemplateId
              }
              onChange={(e) => {
                const id =
                  e.target.value;
                setSelectedTemplateId(
                  id
                );
                const found = (
                  templates ||
                  []
                ).find(
                  (t) =>
                    String(
                      t.id
                    ) ===
                    String(
                      id
                    )
                );
                if (found) {
                  const list =
                    parseWhatsAppTemplates(
                      found.whatsapp_templates
                    );
                  setWaList(
                    list
                  );

                  // compat: usa el primer texto como preview
                  const firstText =
                    list[0]
                      ?.text ||
                    found.content ||
                    "";
                  setTemplate(
                    firstText
                  );

                  // Email
                  setSendEmail(
                    !!found.is_email
                  );
                  setTemplateEmailSubject(
                    found.email_subject ||
                      ""
                  );
                  setTemplateEmailBody(
                    found.content_email ||
                      ""
                  );
                } else {
                  setWaList(
                    []
                  );
                  setTemplate(
                    ""
                  );
                  setSendEmail(
                    false
                  );
                  setTemplateEmailSubject(
                    ""
                  );
                  setTemplateEmailBody(
                    ""
                  );
                }
              }}
            >
              <option value="">
                — Selecciona —
              </option>
              {visibleTemplates.map(
                (t) => (
                  <option
                    key={t.id}
                    value={t.id}
                  >
                    {t.name}
                    {!isBaileys ? " · aprobada Meta" : ""}
                  </option>
                )
              )}
            </select>
            {!isBaileys && visibleTemplates.length === 0 && (
              <small className="muted">
                No hay plantillas Cloud aprobadas. Crea una en Plantillas y usa Enviar a Meta / Sincronizar Meta.
              </small>
            )}
          </label>
        </div>

        <TemplatePlantilla
          // compat anterior:
          template={template}
          setTemplate={setTemplate}
          columns={columns}
          showEmail={sendEmail}
          emailSubject={
            templateEmailSubject
          }
          emailBody={
            templateEmailBody
          }
          // NUEVO: edición de whatsapp_templates
          whatsappTemplates={waList}
          onWhatsappTemplatesChange={
            setWaList
          }
        />
      </section>

      {/* Importar Excel + Enviar */}
      <section className="card section">
        <h3
          style={{ marginTop: 0 }}
        >
          1. Importar Excel
          y enviar
        </h3>

        <input
          key={fileKey}
          ref={fileInputRef}
          className="input"
          type="file"
          accept=".xlsx,.xls"
          onClick={(e) => {
            e.currentTarget.value =
              "";
          }}
          onChange={handleFile}
        />

        {columns.length >
          0 &&
          excelRows.length >
            0 && (
            <>
              <div
                className="muted"
                style={{
                  marginTop: 8,
                }}
              >
                Archivo:{" "}
                <b>
                  {lastSelectedFileName ||
                    "—"}
                </b>{" "}
                · Filas:{" "}
                <b>
                  {
                    excelRows.length
                  }
                </b>
              </div>
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  gap: 8,
                  flexWrap:
                    "wrap",
                }}
              >
                <button
                  className="btn btn-primary"
                  onClick={
                    createImportAndSend
                  }
                  disabled={
                    uploading ||
                    sending ||
                    (!connected && !sendEmail)
                  }
                >
                  {uploading ||
                  sending
                    ? "Procesando..."
                    : "Enviar masivo"}
                </button>
                <button
                  className="btn btn-muted"
                  onClick={() => {
                    setExcelRows(
                      []
                    );
                    setColumns(
                      []
                    );
                    setLastSelectedFileName(
                      ""
                    );
                    if (
                      fileInputRef.current
                    )
                      fileInputRef.current.value =
                        "";
                    setFileKey(
                      (k) =>
                        k + 1
                    );
                  }}
                >
                  Quitar
                  selección
                </button>
              </div>
              <div
                style={{
                  overflowX:
                    "auto",
                  marginTop: 12,
                }}
              >
                <table>
                  <thead>
                    <tr>
                      {columns.map(
                        (
                          c
                        ) => (
                          <th
                            key={
                              c
                            }
                          >
                            {
                              c
                            }
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {excelRows
                      .slice(
                        0,
                        50
                      )
                      .map(
                        (
                          row,
                          i
                        ) => (
                          <tr
                            key={
                              i
                            }
                          >
                            {columns.map(
                              (
                                c
                              ) => (
                                <td
                                  key={
                                    c
                                  }
                                >
                                  {row[
                                    c
                                  ]}
                                </td>
                              )
                            )}
                          </tr>
                        )
                      )}
                  </tbody>
                </table>
                <small
                  style={{
                    color:
                      "var(--muted)",
                  }}
                >
                  Vista
                  previa:
                  primeras
                  50 filas.
                </small>
              </div>
            </>
          )}
      </section>

      {/* Progreso / ETA */}
      {currentBatchId && (
        <section className="card section">
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems:
                "center",
              justifyContent:
                "space-between",
              flexWrap:
                "wrap",
            }}
          >
            <h3
              style={{
                margin: 0,
              }}
            >
              3. Progreso lote #
              {currentBatchId}
            </h3>
            <div className="muted">
              Total:{" "}
              <b>
                {
                  progress.total
                }
              </b>{" "}
              · Enviados:{" "}
              <b>
                {
                  progress.sent
                }
              </b>{" "}
              · Pendientes:{" "}
              <b>
                {
                  progress.pending
                }
              </b>{" "}
              · Errores:{" "}
              <b>
                {
                  progress.error
                }
              </b>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
            }}
          >
            <div
              style={{
                height: 10,
                background:
                  "#111827",
                borderRadius: 6,
              }}
            >
              <div
                style={{
                  height: 10,
                  width: `${
                    progress.total
                      ? Math.min(
                          100,
                          Math.round(
                            (progress.sent /
                              progress.total) *
                              100
                          )
                        )
                      : 0
                  }%`,
                  background:
                    "#22c55e",
                  borderRadius: 6,
                  transition:
                    "width .3s",
                }}
              />
            </div>
            <div
              className="muted"
              style={{
                marginTop: 8,
              }}
            >
              {progress.inProgress ? (
                <>
                  ETA:{" "}
                  <b>
                    {HHMMSS(
                      eta.seconds
                    )}
                  </b>
                  {eta.finishAt && (
                    <>
                      {" "}
                      · Fin
                      aprox:{" "}
                      <b>
                        {eta.finishAt.toLocaleString()}
                      </b>
                    </>
                  )}
                </>
              ) : (
                <>
                  Estado:{" "}
                  <b>
                    {currentBatchMeta?.status ||
                      "—"}
                  </b>
                </>
              )}
            </div>

            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 8,
                flexWrap:
                  "wrap",
              }}
            >
              {progress.inProgress && (
                <>
                  <button
                    className="btn btn-warning"
                    onClick={() =>
                      ep.batchStop(
                        selectedId,
                        currentBatchId,
                        {
                          abandon: false,
                        }
                      )
                    }
                  >
                    Pausar
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={
                      stopSending
                    }
                  >
                    Abandonar
                  </button>
                </>
              )}

              {!progress.inProgress &&
                (currentBatchMeta?.status ===
                  "paused" ||
                  currentBatchMeta?.status ===
                    "created") && (
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      if (
                        !selectedId ||
                        !currentBatchId
                      )
                        return;
                      const perMessageDelayMs =
                        Math.max(
                          0,
                          Math.round(
                            perMessageDelaySec *
                              1000
                          )
                        );
                      const batchPauseMs =
                        Math.max(
                          0,
                          Math.round(
                            batchPauseMin *
                              60 *
                              1000
                          )
                        );
                      await ep.batchStart(
                        selectedId,
                        currentBatchId,
                        {
                          perMessageDelayMs,
                          batchSize,
                          batchPauseMs,
                          userId:
                            user?.id,
                          sendEmail,
                        }
                      );
                    }}
                  >
                    Reanudar
                  </button>
                )}

              {(currentBatchMeta?.status ===
                "finished" ||
                (!progress.inProgress &&
                  remaining ===
                    0)) && (
                <button
                  className="btn"
                  onClick={
                    clearViewForNew
                  }
                >
                  Nuevo
                  envío
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Detalle jobs */}
      {jobsOpen &&
        currentBatchId && (
          <section className="card section">
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
                flexWrap:
                  "wrap",
              }}
            >
              <h3
                style={{
                  margin: 0,
                }}
              >
                4. Teléfonos
                del lote #
                {currentBatchId}
              </h3>
              <div className="muted">
                Total:{" "}
                <b>
                  {
                    jobsCounts.total
                  }
                </b>{" "}
                · Enviados:{" "}
                <b>
                  {
                    jobsCounts.sent
                  }
                </b>{" "}
                · Pendientes:{" "}
                <b>
                  {
                    jobsCounts.pending
                  }
                </b>{" "}
                · Errores:{" "}
                <b>
                  {
                    jobsCounts.error
                  }
                </b>
              </div>
            </div>

            <div
              className="row row-3"
              style={{
                marginTop: 10,
              }}
            >
              <label>
                Estado
                <select
                  className="input"
                  value={
                    jobsStatus
                  }
                  onChange={async (
                    e
                  ) => {
                    setJobsStatus(
                      e
                        .target
                        .value
                    );
                    await loadJobs(
                      currentBatchId
                    );
                  }}
                >
                  <option value="all">
                    Todos
                  </option>
                  <option value="pending">
                    Pendientes
                  </option>
                  <option value="sent">
                    Enviados
                  </option>
                  <option value="error">
                    Errores
                  </option>
                </select>
              </label>
              <label>
                Buscar
                (teléfono o
                datos)
                <input
                  className="input"
                  value={
                    jobsQuery
                  }
                  onChange={(e) =>
                    setJobsQuery(
                      e
                        .target
                        .value
                    )
                  }
                  onKeyDown={async (
                    e
                  ) => {
                    if (
                      e.key ===
                      "Enter"
                    ) {
                      await loadJobs(
                        currentBatchId
                      );
                    }
                  }}
                  placeholder="Ej: +57 o 'Juan'"
                />
              </label>
              <div
                style={{
                  display:
                    "flex",
                  alignItems:
                    "end",
                  gap: 8,
                }}
              >
                <button
                  className="btn"
                  onClick={() =>
                    loadJobs(
                      currentBatchId
                    )
                  }
                  disabled={
                    jobsLoading
                  }
                >
                  {jobsLoading
                    ? "Cargando..."
                    : "Actualizar"}
                </button>
                <button
                  className="btn btn-muted"
                  onClick={async () => {
                    setJobsStatus(
                      "all"
                    );
                    setJobsQuery(
                      ""
                    );
                    await loadJobs(
                      currentBatchId
                    );
                  }}
                >
                  Limpiar
                  filtros
                </button>
              </div>
            </div>

            <div
              style={{
                overflowX:
                  "auto",
                marginTop: 12,
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>
                      Teléfono
                    </th>
                    <th>
                      Status
                    </th>
                    <th>
                      WA
                    </th>
                    <th>
                      Error
                    </th>
                    <th>
                      Enviado
                    </th>
                    <th>
                      Mensaje
                      (preview)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan={
                          9
                        }
                      >
                        <div className="muted">
                          Sin
                          resultados
                          para el
                          filtro
                          actual.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    jobs.map(
                      (
                        r
                      ) => (
                        <tr
                          key={
                            r.id
                          }
                        >
                          <td>
                            {
                              r.id
                            }
                          </td>
                          <td>
                            {
                              r.phone
                            }
                          </td>
                          <td
                            style={{
                              color:
                                r.status ===
                                "sent"
                                  ? "#22c55e"
                                  : r.status ===
                                    "error"
                                  ? "#ef4444"
                                  : "#e5e7eb",
                            }}
                          >
                            {
                              r.status
                            }
                          </td>
                          <td>
                            {r.wa_status ||
                              ""}
                          </td>
                          <td>
                            {r.error_msg ||
                              ""}
                          </td>
                          <td>
                            {r.sent_at ||
                              ""}
                          </td>
                          <td>
                            <code
                              style={{
                                whiteSpace:
                                  "pre-wrap",
                              }}
                            >
                              {r.message_preview ||
                                ""}
                            </code>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
    </>
  );
}
